import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Chip
} from '@mui/material';
import './OrderDetails.css';
import { userRequest } from '../../../RequestMethods';
import OrderProduct from '../../../Components/OrderProduct/OrderProduct.js';
import { OrderCycleTimelineCard } from '../../../Components/OrderCycleTimeline/OrderCycleTimeline';

const getOrderProducts = (order) =>
    Array.isArray(order?.products) ? order.products : [];

const getOrderItemType = (item) =>
    item?.itemType || (item?.designId ? 'design' : 'finished');

const getOrderItemDesignId = (item) => {
    const itemType = getOrderItemType(item);
    return item?.designId || (itemType === 'design' ? item?._id : null);
};

const getOrderItemProductId = (item) => {
    const itemType = getOrderItemType(item);
    return item?.productId || (itemType === 'finished' ? item?._id : null);
};

const getOrderProductTitle = (item) =>
    item?.title ||
    item?.designData?.title ||
    item?.design?.title ||
    item?.productData?.title ||
    item?.product?.title ||
    item?.productId ||
    item?.designId ||
    item?._id ||
    'Article commande';

const OrderDetails = ({ open, onClose, orderId, orderData }) => {
    const [order, setOrder] = useState(orderData || null);
    const [loading, setLoading] = useState(!orderData);
    const [designsData, setDesignsData] = useState({});
    const [productsData, setProductsData] = useState({});

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                setLoading(true);
                const res = await userRequest.get(`orders/${orderId}`);
                setOrder(res.data.order);
            } catch (err) {
                console.error("Error fetching order details:", err);
            } finally {
                setLoading(false);
            }
        };

        if (open && !orderData && orderId) {
            fetchOrderDetails();
        } else if (orderData) {
            setOrder(orderData);
            setLoading(false);
        }
    }, [open, orderId, orderData]);

    const orderProducts = useMemo(() => getOrderProducts(order), [order]);

    const designIds = useMemo(() => {
        return [
            ...new Set(
                orderProducts
                    .map(getOrderItemDesignId)
                    .filter(Boolean)
            )
        ];
    }, [orderProducts]);

    const productIds = useMemo(() => {
        return [
            ...new Set(
                orderProducts
                    .map(getOrderItemProductId)
                    .filter(Boolean)
            )
        ];
    }, [orderProducts]);

    useEffect(() => {
        const fetchAllDesigns = async () => {
            if (designIds.length === 0) {
                setDesignsData({});
                return;
            }
            
            try {
                const designPromises = designIds.map(id => 
                    userRequest
                        .get(`designs/${id}`)
                        .then(res => ({ id, data: res.data.design }))
                        .catch(err => {
                            if (err.response?.status === 404) return null;
                            throw err;
                        })
                );
                const designResults = await Promise.all(designPromises);
                
                const designsMap = {};
                designResults.forEach(result => {
                    if (result?.data) {
                        designsMap[result.id] = result.data;
                    }
                });
                setDesignsData(designsMap);
            } catch (err) {
                console.error("Error fetching designs:", err);
            }
        };
        
        fetchAllDesigns();
    }, [designIds]);

    useEffect(() => {
        const fetchAllProducts = async () => {
            if (productIds.length === 0) {
                setProductsData({});
                return;
            }
            
            try {
                const productPromises = productIds.map(id => 
                    userRequest
                        .get(`products/${id}`)
                        .then(res => ({ id, data: res.data.product }))
                        .catch(err => {
                            if (err.response?.status === 404) return null;
                            throw err;
                        })
                );
                const productResults = await Promise.all(productPromises);
                
                const productsMap = {};
                productResults.forEach(result => {
                    if (result?.data) {
                        productsMap[result.id] = result.data;
                    }
                });
                setProductsData(productsMap);
            } catch (err) {
                console.error("Error fetching products:", err);
            }
        };
        
        fetchAllProducts();
    }, [productIds]);

    const getStatusLabel = (status) => {
        const statusMap = {
            'reçu': 'Reçu',
            'prêt': 'Prêt',
            'livré': 'Livré',
            'annulé': 'Annulé'
        };
        return statusMap[status] || status;
    };

    const getStatusStyle = (status) => {
        const styles = {
            'reçu': {
                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 193, 7, 0.1))',
                color: '#FF8F00',
                border: '1px solid rgba(255, 152, 0, 0.3)'
            },
            'prêt': {
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(129, 199, 132, 0.1))',
                color: '#43A047',
                border: '1px solid rgba(76, 175, 80, 0.3)'
            },
            'livré': {
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(236, 72, 153, 0.1))',
                color: '#a855f7',
                border: '1px solid rgba(168, 85, 247, 0.3)'
            },
            'annulé': {
                background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(229, 115, 115, 0.1))',
                color: '#D32F2F',
                border: '1px solid rgba(244, 67, 54, 0.3)'
            }
        };
        return styles[status] || styles['reçu'];
    };

    if (loading) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogContent>
                    <div className="modern-loading">
                        <div className="loading-spinner"></div>
                        <Typography>Chargement des détails...</Typography>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    if (!order) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogContent>
                    <div className="modern-error">
                        <span className="error-icon">🔍</span>
                        <Typography>Commande non trouvée</Typography>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="lg" 
            fullWidth
            className="modern-dialog"
        >
            <DialogTitle className="modern-title">
                <div className="title-modern">
                    <div className="title-glow"></div>
                    <span className="title-icon">✨</span>
                    <span>Détails de la commande</span>
                </div>
                <IconButton onClick={onClose} className="modern-close">
                    ✕
                </IconButton>
            </DialogTitle>
            
            <DialogContent className="modern-content">
                {/* Order status timeline */}
                <div className="modern-card glass-effect">
                    <div className="card-header-modern">
                        <div className="header-gradient"></div>
                        <div className="header-content">
                            <h3>Suivi de la commande</h3>
                        </div>
                    </div>
                    <div className="card-body-modern">
                        <OrderCycleTimelineCard status={order.status} />
                    </div>
                </div>

                {/* Order Summary - Modern Card */}
                <div className="modern-card glass-effect">
                    <div className="card-header-modern">
                        <div className="header-gradient"></div>
                        <div className="header-content">
                            <span className="header-icon">📊</span>
                            <h3>Informations générales</h3>

                        </div>
                    </div>
                    <div className="card-body-modern">
                        <div className="stats-grid">
                            <div className="stat-item">
                                <div className="stat-icon">🆔</div>
                                <div className="stat-info">
                                    <label>ID Commande</label>
                                    <span className="stat-value code">{order._id}</span>
                                </div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-icon">📅</div>
                                <div className="stat-info">
                                    <label>Date</label>
                                    <span className="stat-value">{new Date(order.createdAt).toLocaleString()}</span>
                                </div>
                            </div>
                            <div className="stat-item">
                                <div className="stat-icon">📌</div>
                                <div className="stat-info">
                                    <label>Statut</label>
                                    <Chip 
                                        label={getStatusLabel(order.status)}
                                        style={{
                                            background: getStatusStyle(order.status).background,
                                            color: getStatusStyle(order.status).color,
                                            border: getStatusStyle(order.status).border,
                                            fontWeight: 600,
                                            backdropFilter: 'blur(10px)'
                                        }}
                                    />
                                </div>
                            </div>
                            {order.productionStatus && (
                                <div className="stat-item">
                                    <div className="stat-icon">🏭</div>
                                    <div className="stat-info">
                                        <label>Statut production</label>
                                        <Chip
                                            label={order.productionStatus === 'en_cours' ? 'En cours' : order.productionStatus}
                                            style={{
                                                background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 193, 7, 0.1))',
                                                color: '#FF8F00',
                                                border: '1px solid rgba(255, 152, 0, 0.3)',
                                                fontWeight: 600,
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="stat-item highlight">
                                <div className="stat-icon">💰</div>
                                <div className="stat-info">
                                    <label>Total</label>
                                    <span className="stat-value total">€ {order.total?.toFixed(2) || '0.00'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Client Information - Modern Card */}
                <div className="modern-card glass-effect">
                    <div className="card-header-modern">
                        <div className="header-gradient"></div>
                        <div className="header-content">
                            <span className="header-icon">👤</span>
                            <h3>Informations client</h3>
                        </div>
                    </div>
                    <div className="card-body-modern">
                        <div className="client-grid">
                            <div className="client-info-item">
                                <div className="info-label">🏢 Entreprise</div>
                                <div className="info-value">{order.company || 'N/A'}</div>
                            </div>
                            <div className="client-info-item">
                                <div className="info-label">📧 Email</div>
                                <div className="info-value">{order.email || 'N/A'}</div>
                            </div>
                            <div className="client-info-item">
                                <div className="info-label">📞 Téléphone</div>
                                <div className="info-value">{order.number || 'N/A'}</div>
                            </div>
                            <div className="client-info-item">
                                <div className="info-label">📍 Adresse</div>
                                <div className="info-value">{order.address || 'N/A'}</div>
                            </div>
                            <div className="client-info-item">
                                <div className="info-label">🏙️ Ville</div>
                                <div className="info-value">{order.city || 'N/A'}</div>
                            </div>
                            <div className="client-info-item">
                                <div className="info-label">📮 Code postal</div>
                                <div className="info-value">{order.postalCode || 'N/A'}</div>
                            </div>
                            <div className="client-info-item">
                                <div className="info-label">🗺️ Département</div>
                                <div className="info-value">{order.country || 'N/A'}</div>
                            </div>
                        </div>
                        {order.notes && (
                            <div className="notes-modern">
                                <div className="notes-icon">📝</div>
                                <div className="notes-content">
                                    <label>Notes</label>
                                    <p>{order.notes}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Stock Shortfall - Modern Card */}
                {order.stockShortfall?.length > 0 && (
                    <div className="modern-card glass-effect">
                        <div className="card-header-modern">
                            <div className="header-gradient"></div>
                            <div className="header-content">
                                <span className="header-icon">⚠️</span>
                                <h3>Rupture de stock</h3>
                            </div>
                        </div>
                        <div className="card-body-modern">
                            <Typography style={{ marginBottom: '12px', color: '#B02A37' }}>
                                Cette commande a été acceptée mais les lignes suivantes n'ont pas pu être réservées faute de stock disponible. Le client n'en a pas été informé.
                            </Typography>
                            <div className="client-grid">
                                {order.stockShortfall.map((line, index) => (
                                    <div key={index} className="client-info-item">
                                        <div className="info-label">
                                            {line.productTitle || line.productId}
                                        </div>
                                        <div className="info-value">
                                            {[
                                                line.color ? `Couleur: ${line.color}` : '',
                                                line.size ? `Taille: ${line.size}` : '',
                                                `Quantité demandée: ${line.quantityRequested}`,
                                            ].filter(Boolean).join(' | ')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Products Section - Modern Card */}
                <div className="modern-card glass-effect">
                    <div className="card-header-modern">
                        <div className="header-gradient"></div>
                        <div className="header-content">
                            <span className="header-icon">🛍️</span>
                            <h3>Produits commandés</h3>
                            <span className="product-count">{orderProducts.length} articles</span>
                        </div>
                    </div>
                    <div className="products-container">
                        {orderProducts.length > 0 ? (
                            <div className="products-scroll">
                                {orderProducts.map((product, index) => {
                                    const itemType = getOrderItemType(product);
                                    const designId = getOrderItemDesignId(product);
                                    const productId = getOrderItemProductId(product);
                                    const designData =
                                        product.designData ||
                                        product.design ||
                                        designsData[designId];
                                    const productData =
                                        product.productData ||
                                        product.product ||
                                        productsData[productId];
                                    
                                    if (!productData) {
                                        return (
                                            <div key={`${product._id || index}-missing`} className="order-product-fallback">
                                                <strong>{getOrderProductTitle(product)}</strong>
                                                <span>Details produit indisponibles</span>
                                                <small>
                                                    {[
                                                        itemType ? `Type: ${itemType}` : '',
                                                        product.color ? `Couleur: ${product.color}` : '',
                                                        product.size ? `Taille: ${product.size}` : '',
                                                        product.quantity ? `Quantite: ${product.quantity}` : ''
                                                    ].filter(Boolean).join(' | ')}
                                                </small>
                                            </div>
                                        );
                                    }
                                    
                                    return (
                                        <OrderProduct 
                                            key={`${product._id}-${product.color}-${product.side}-${index}`}
                                            id={product._id || designId || productId}
                                            color={product.color}
                                            quantity={product.quantity}
                                            side={product.side}
                                            size={product.size}
                                            itemType={itemType}
                                            designData={designData}
                                            productData={productData}
                                            pack={product.pack}
                                            price={product.price}
                                            selectedSizes={product.selectedSizes}
                                        />
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-products">
                                <span>🛒</span>
                                <p>Aucun produit trouvé</p>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
            
            <DialogActions className="modern-actions">
                <Button onClick={onClose} className="modern-button">
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderDetails;
